import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, User, Briefcase, GraduationCap, Heart, Target, CheckCircle, Send, ArrowLeft, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';
import SEO, { SEOPages } from '../components/SEO';
import { loadUserDataCache, saveUserDataCache } from '../utils/userDataCache';

import API_BASE_URL from '../config/api';
const API = API_BASE_URL;

// Load initial form data from shared cache
const getInitialFormData = () => {
  const cached = loadUserDataCache();
  return {
    nom: cached.nom || '',
    prenom: cached.prenom || '',
    email: cached.email || '',
    telephone: cached.telephone || '',
    poste_interesse: '',
    experience: '',
    formation: '',
    motivation: '',
    disponibilite: '',
    mobilite: '',
    salaire_souhaite: '',
    cv_url: '',
    linkedin_url: ''
  };
};

const Recrutement = () => {
  const [formData, setFormData] = useState(getInitialFormData);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Save to shared cache when user data changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.nom || formData.prenom || formData.email || formData.telephone) {
        saveUserDataCache({
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone
        });
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.nom, formData.prenom, formData.email, formData.telephone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/recrutement`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
      } else {
        setError('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('Erreur envoi candidature:', err);
      setError('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isSubmitted) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="glass p-12 rounded-3xl">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h1 className="text-4xl font-bold heading-display text-white mb-6">
                Candidature envoyée avec succès !
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Merci pour votre candidature. Nous étudierons votre profil avec attention et vous recontacterons rapidement.
              </p>
              <p className="text-lg text-blue-300 mb-8">
                Un accusé de réception a été envoyé à l'adresse : <strong>{formData.email}</strong>
              </p>
              <div className="glass p-6 rounded-2xl">
                <h3 className="text-lg font-semibold text-white mb-4">Prochaines étapes :</h3>
                <ul className="text-left text-gray-300 space-y-2">
                  <li>• Étude de votre candidature sous 48h</li>
                  <li>• Premier entretien téléphonique si sélectionné</li>
                  <li>• Rencontre en personne avec l'équipe</li>
                  <li>• Processus d'intégration personnalisé</li>
                </ul>
              </div>
              <div className="mt-8">
                <Link to="/a-propos" className="btn-glass btn-primary px-8 py-3">
                  <ArrowLeft className="mr-2" size={20} />
                  Retour à A propos
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO {...SEOPages.recrutement} />
      <div className="pt-20">
      {/* Hero */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold heading-display text-white mb-6">
              Rejoignez l'équipe ATOM Group
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Vous souhaitez évoluer dans le conseil financier ? Rejoignez une équipe dynamique et ambitieuse 
              qui place l'humain au cœur de ses préoccupations.
            </p>
            <div className="flex justify-center">
              <Link to="/a-propos" className="btn-glass px-6 py-3 flex items-center">
                <ArrowLeft className="mr-2" size={20} />
                Retour à A propos
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi nous rejoindre */}
      <section className="pb-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Formulaire */}
            <div className="glass p-8 rounded-3xl">
              <h2 className="text-2xl font-bold heading-display text-white mb-8 flex items-center">
                <User className="mr-3" size={28} />
                Postuler chez ATOM Group
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="glass p-4 rounded-xl border border-red-500/50 bg-red-500/10">
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                )}

                {/* Informations personnelles */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <User className="mr-2" size={20} />
                    Informations personnelles
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Nom *
                      </label>
                      <input
                        type="text"
                        name="nom"
                        required
                        value={formData.nom}
                        onChange={handleChange}
                        className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300"
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        name="prenom"
                        required
                        value={formData.prenom}
                        onChange={handleChange}
                        className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300"
                        placeholder="Votre prénom"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300"
                        placeholder="votre@email.ch"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        required
                        value={formData.telephone}
                        onChange={handleChange}
                        className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300"
                        placeholder="+41 XX XXX XX XX"
                      />
                    </div>
                  </div>
                </div>

                {/* Informations professionnelles */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Briefcase className="mr-2" size={20} />
                    Parcours professionnel
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Poste qui vous intéresse *
                    </label>
                    <select
                      name="poste_interesse"
                      required
                      value={formData.poste_interesse}
                      onChange={handleChange}
                      className="w-full p-3 glass border-0 rounded-xl text-white focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="">Sélectionnez un poste</option>
                      <option value="Conseiller financier junior" className="bg-gray-800">Conseiller financier junior</option>
                      <option value="Conseiller financier senior" className="bg-gray-800">Conseiller financier senior</option>
                      <option value="Spécialiste en assurances" className="bg-gray-800">Spécialiste en assurances</option>
                      <option value="Spécialiste en prévoyance" className="bg-gray-800">Spécialiste en prévoyance</option>
                      <option value="Assistant administratif" className="bg-gray-800">Assistant administratif</option>
                      <option value="Stagiaire" className="bg-gray-800">Stagiaire</option>
                      <option value="Autre" className="bg-gray-800">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Années d'expérience dans le domaine financier *
                    </label>
                    <select
                      name="experience"
                      required
                      value={formData.experience}
                      onChange={handleChange}
                      className="w-full p-3 glass border-0 rounded-xl text-white focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="">Sélectionnez votre expérience</option>
                      <option value="Débutant (0-1 an)" className="bg-gray-800">Débutant (0-1 an)</option>
                      <option value="Junior (1-3 ans)" className="bg-gray-800">Junior (1-3 ans)</option>
                      <option value="Confirmé (3-7 ans)" className="bg-gray-800">Confirmé (3-7 ans)</option>
                      <option value="Senior (7-15 ans)" className="bg-gray-800">Senior (7-15 ans)</option>
                      <option value="Expert (15+ ans)" className="bg-gray-800">Expert (15+ ans)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Formation *
                    </label>
                    <input
                      type="text"
                      name="formation"
                      required
                      value={formData.formation}
                      onChange={handleChange}
                      className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300"
                      placeholder="Ex: Master en économie, Brevet fédéral..."
                    />
                  </div>
                </div>

                {/* Motivation et disponibilité */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Heart className="mr-2" size={20} />
                    Motivation & Disponibilité
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Qu'est-ce qui vous motive à rejoindre ATOM Group SA ? *
                    </label>
                    <textarea
                      name="motivation"
                      required
                      rows="4"
                      value={formData.motivation}
                      onChange={handleChange}
                      className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300 resize-none"
                      placeholder="Partagez votre motivation, vos objectifs professionnels, ce qui vous attire chez nous..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Disponibilité *
                      </label>
                      <select
                        name="disponibilite"
                        required
                        value={formData.disponibilite}
                        onChange={handleChange}
                        className="w-full p-3 glass border-0 rounded-xl text-white focus:ring-2 focus:ring-blue-300"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="Immédiate" className="bg-gray-800">Immédiate</option>
                        <option value="1 mois" className="bg-gray-800">Dans 1 mois</option>
                        <option value="2-3 mois" className="bg-gray-800">Dans 2-3 mois</option>
                        <option value="À convenir" className="bg-gray-800">À convenir</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Mobilité géographique
                      </label>
                      <select
                        name="mobilite"
                        value={formData.mobilite}
                        onChange={handleChange}
                        className="w-full p-3 glass border-0 rounded-xl text-white focus:ring-2 focus:ring-blue-300"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="Neuchâtel uniquement" className="bg-gray-800">Neuchâtel uniquement</option>
                        <option value="Canton de Neuchâtel" className="bg-gray-800">Canton de Neuchâtel</option>
                        <option value="Suisse romande" className="bg-gray-800">Suisse romande</option>
                        <option value="Toute la Suisse" className="bg-gray-800">Toute la Suisse</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Prétentions salariales (optionnel)
                    </label>
                    <input
                      type="text"
                      name="salaire_souhaite"
                      value={formData.salaire_souhaite}
                      onChange={handleChange}
                      className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300"
                      placeholder="Ex: 80'000 CHF/an ou À négocier"
                    />
                  </div>
                </div>

                {/* Documents */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <GraduationCap className="mr-2" size={20} />
                    Liens utiles
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Lien vers votre CV (Google Drive, Dropbox, etc.)
                    </label>
                    <input
                      type="url"
                      name="cv_url"
                      value={formData.cv_url}
                      onChange={handleChange}
                      className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300"
                      placeholder="https://drive.google.com/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Profil LinkedIn (optionnel)
                    </label>
                    <input
                      type="url"
                      name="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={handleChange}
                      className="w-full p-3 glass border-0 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-300"
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-glass btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Envoyer ma candidature
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Pourquoi nous rejoindre */}
            <div className="space-y-8">
              <div className="glass p-8 rounded-3xl">
                <h3 className="text-2xl font-bold heading-display text-white mb-6 flex items-center">
                  <Target className="mr-3" size={28} />
                  Pourquoi ATOM Group SA ?
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="text-white" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Entreprise en croissance</h4>
                      <p className="text-gray-300 text-sm">
                        Rejoignez une société dynamique en pleine expansion avec de belles opportunités d'évolution.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="text-white" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Formation continue</h4>
                      <p className="text-gray-300 text-sm">
                        Développez vos compétences avec nos programmes de formation et certifications professionnelles.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="text-white" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Esprit d'équipe</h4>
                      <p className="text-gray-300 text-sm">
                        Intégrez une équipe passionnée où collaboration et bienveillance sont au cœur de nos valeurs.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Heart className="text-white" size={20} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-2">Impact positif</h4>
                      <p className="text-gray-300 text-sm">
                        Participez à une mission noble : aider nos clients à sécuriser leur avenir financier.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass p-8 rounded-3xl">
                <h3 className="text-xl font-bold text-white mb-4">
                  Ce que nous offrons
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Salaire compétitif et commissions attractives
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Avantages sociaux complets (LPP, assurances)
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Horaires flexibles et télétravail possible
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Formation initiale et continue prise en charge
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Environnement de travail moderne et stimulant
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-green-400 mr-3 mt-1 flex-shrink-0" />
                    Opportunités d'évolution et de spécialisation
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Recrutement;